import { Box, Divider, HStack, Image, Stack, VStack } from '@chakra-ui/react';

import { CVData, CVSection, CVSettings, GDPR_CONSENT_TEXT } from '../types';
import { getDisplayPhotoUrl, hexToRgba } from '../utils/cvDefaults';
import { buildContactLinks, filterEmptyEntries, getDuplicateTitles, parseDescription } from '../utils/pdfUtils';

interface TemplateProps {
  data: CVData;
  settings: CVSettings;
}

const FONT_SIZE_MAP = {
  small: { name: '18px', title: '11px', body: '9px', section: '11px', gdpr: '7px' },
  medium: { name: '22px', title: '12px', body: '10px', section: '12px', gdpr: '7.5px' },
  large: { name: '26px', title: '14px', body: '11px', section: '14px', gdpr: '8px' },
};

const SIDEBAR_SECTION_TYPES = ['skills', 'languages', 'certifications', 'hobbies', 'interests'];

const ModernTemplate = ({ data, settings }: TemplateProps) => {
  const { personalInfo, sections } = data;
  const sizes = FONT_SIZE_MAP[settings.fontSize];
  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);
  const hasBg = settings.headerBackground;
  const accentBg = hexToRgba(settings.accentColor, settings.accentOpacity ?? 1);

  const sidebarSections = sections.filter((s) => SIDEBAR_SECTION_TYPES.includes(s.type));
  const mainSections = sections.filter((s) => !SIDEBAR_SECTION_TYPES.includes(s.type));

  const sidebarColor = (light: string, dark: string) => (isSidebar: boolean) =>
    isSidebar ? (hasBg ? light : dark) : dark;

  const renderSection = (section: CVSection, isSidebar = false) => {
    const sc = (light: string, dark: string) => sidebarColor(light, dark)(isSidebar);

    return (
      <Box key={section.id} mb={4}>
        <Box
          fontSize={sizes.section}
          fontWeight="bold"
          color={sc('white', '#1A202C')}
          letterSpacing="1px"
          mb={1}
        >
          {section.title}
        </Box>
        <Divider borderColor={sc('whiteAlpha.300', settings.accentColor)} opacity={0.3} mb={2} />

        {section.type === 'skills' ? (() => {
          const filtered = filterEmptyEntries(section.entries);
          const dupes = getDuplicateTitles(filtered);
          const categorized = filtered.reduce<Record<string, string[]>>((acc, entry) => {
            const category = entry.subtitle || 'General';
            if (!acc[category]) acc[category] = [];
            acc[category].push(entry.title);
            return acc;
          }, {});
          const categories = Object.entries(categorized);
          return (
            <Stack spacing={2}>
              {categories.map(([category, skills]) => (
                <Box key={category}>
                  {categories.length > 1 && category !== 'General' && (
                    <Box fontSize={sizes.body} fontWeight="semibold" color={sc('whiteAlpha.900', 'gray.700')} mb={1}>
                      {category}
                    </Box>
                  )}
                  <HStack spacing={1.5} flexWrap="wrap">
                    {skills.map((skill, i) => {
                      const isDupe = dupes.has(skill.trim().toLowerCase());
                      return (
                        <Box
                          key={i}
                          fontSize={sizes.body}
                          px={2.5}
                          py={0.5}
                          backgroundColor={isDupe ? 'red.100' : sc('whiteAlpha.200', 'gray.100')}
                          borderRadius="full"
                          color={isDupe ? 'red.600' : sc('whiteAlpha.900', 'gray.700')}
                          mb={1}
                        >
                          {skill}
                        </Box>
                      );
                    })}
                  </HStack>
                </Box>
              ))}
            </Stack>
          );
        })() : section.type === 'hobbies' || section.type === 'interests' ? (
          (() => {
            const filtered = filterEmptyEntries(section.entries);
            const dupes = getDuplicateTitles(filtered);
            return (
              <HStack spacing={1.5} flexWrap="wrap">
                {filtered.map((entry) => {
                  const isDupe = dupes.has(entry.title.trim().toLowerCase());
                  return (
                    <Box
                      key={entry.id}
                      fontSize={sizes.body}
                      px={2.5}
                      py={0.5}
                      backgroundColor={isDupe ? 'red.100' : sc('whiteAlpha.200', 'gray.100')}
                      borderRadius="full"
                      color={isDupe ? 'red.600' : sc('whiteAlpha.900', 'gray.700')}
                      mb={1}
                    >
                      {entry.title}
                    </Box>
                  );
                })}
              </HStack>
            );
          })()
        ) : section.type === 'languages' ? (
          <Stack spacing={1}>
            {filterEmptyEntries(section.entries).map((entry) => (
              <HStack key={entry.id} spacing={1} fontSize={sizes.body}>
                <Box fontWeight="semibold" color={sc('whiteAlpha.900', 'gray.700')}>
                  {entry.title}
                </Box>
                {entry.subtitle && (
                  <Box color={sc('whiteAlpha.600', 'gray.400')}>
                    ({entry.subtitle})
                  </Box>
                )}
              </HStack>
            ))}
          </Stack>
        ) : section.type === 'certifications' ? (
          <Stack spacing={2}>
            {filterEmptyEntries(section.entries).map((entry) => (
              <Box key={entry.id}>
                <Box fontSize={sizes.title} fontWeight="semibold" color={sc('white', 'gray.800')}>
                  {entry.title}
                </Box>
                {entry.subtitle && (
                  <Box fontSize={sizes.body} color={sc('whiteAlpha.700', 'gray.600')} fontStyle="italic">
                    {entry.subtitle}
                  </Box>
                )}
                {(entry.startDate || entry.endDate) && (
                  <Box fontSize={sizes.body} color={sc('whiteAlpha.600', 'gray.500')}>
                    {entry.startDate}
                    {entry.endDate && ` – ${entry.endDate}`}
                  </Box>
                )}
                {entry.location && (
                  <Box fontSize={sizes.body} color={sc('whiteAlpha.600', 'gray.500')} mt={0.5}>
                    ID: {entry.location}
                  </Box>
                )}
                {entry.url && (
                  <Box as="a" href={entry.url} fontSize={sizes.body} color={sc('whiteAlpha.800', settings.accentColor)} textDecoration="underline">
                    Verify Credential
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Stack spacing={4}>
            {filterEmptyEntries(section.entries).map((entry) => (
              <Box key={entry.id}>
                <HStack justifyContent="space-between" alignItems="flex-start">
                  <VStack alignItems="flex-start" spacing={0}>
                    <Box fontSize={sizes.title} fontWeight="semibold" color={sc('white', 'gray.800')}>
                      {entry.title}
                    </Box>
                    {entry.subtitle && (
                      <Box fontSize={sizes.body} color={sc('whiteAlpha.700', 'gray.600')} fontStyle="italic">
                        {entry.subtitle}
                        {entry.location && ` — ${entry.location}`}
                      </Box>
                    )}
                  </VStack>
                  {(entry.startDate || entry.endDate) && (
                    <Box fontSize={sizes.body} color={sc('whiteAlpha.600', 'gray.500')} whiteSpace="nowrap">
                      {entry.startDate}
                      {entry.endDate && ` – ${entry.endDate}`}
                    </Box>
                  )}
                </HStack>
                {entry.description && (() => {
                  const segments = parseDescription(entry.description);
                  return (
                    <Box mt={1}>
                      {segments.map((seg, si) => {
                        if (seg.type === 'break') return <Box key={si} height="6px" />;
                        if (seg.type === 'bullet') return (
                          <HStack key={si} spacing={1.5} alignItems="flex-start" mt={0.5} pl={0.5}>
                            <Box as="span" fontSize={sizes.body} color={settings.accentColor} lineHeight="1.5">•</Box>
                            <Box as="span" fontSize={sizes.body} color={sc('whiteAlpha.800', 'gray.600')} lineHeight="1.5">{seg.text}</Box>
                          </HStack>
                        );
                        return (
                          <Box key={si} fontSize={sizes.body} color={sc('whiteAlpha.800', 'gray.600')} lineHeight="1.5">
                            {seg.text}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
                {entry.tags.length > 0 && (
                  <HStack spacing={1} mt={1} flexWrap="wrap">
                    {entry.tags.map((tag) => (
                      <Box
                        key={tag}
                        fontSize="8px"
                        px={1.5}
                        py={0.5}
                        backgroundColor={sc('whiteAlpha.200', 'gray.100')}
                        borderRadius="sm"
                        color={sc('whiteAlpha.800', 'gray.600')}
                      >
                        {tag}
                      </Box>
                    ))}
                  </HStack>
                )}

                {(entry.url || entry.previewUrl) && (
                  <HStack spacing={3} mt={1} fontSize={sizes.body}>
                    {entry.url && (
                      <Box
                        as="a"
                        href={entry.url}
                        color={sc('whiteAlpha.800', settings.accentColor)}
                        textDecoration="underline"
                      >
                        {entry.urlLabel || 'GitHub'}
                      </Box>
                    )}
                    {entry.previewUrl && (
                      <Box
                        as="a"
                        href={entry.previewUrl}
                        color={sc('whiteAlpha.800', settings.accentColor)}
                        textDecoration="underline"
                      >
                        {entry.previewUrlLabel || 'Live Demo'}
                      </Box>
                    )}
                  </HStack>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <HStack
      width="100%"
      minHeight="100%"
      backgroundColor="white"
      fontFamily={settings.fontFamily}
      alignItems="stretch"
      spacing={0}
      flexDirection={settings.sidebarReversed ? 'row-reverse' : 'row'}
      overflowWrap="break-word"
      wordBreak="break-word"
    >
      {/* Sidebar */}
      <Box
        width="35%"
        backgroundColor={hasBg ? accentBg : 'gray.50'}
        p={6}
        color={hasBg ? 'white' : '#1A202C'}
        flexShrink={0}
        borderRight={!hasBg && !settings.sidebarReversed ? '1px solid' : 'none'}
        borderLeft={!hasBg && settings.sidebarReversed ? '1px solid' : 'none'}
        borderColor={!hasBg ? hexToRgba(settings.accentColor, 0.3) : 'transparent'}
      >
        {/* Photo */}
        {settings.showPhoto && displayPhotoUrl && (
          <Box mb={4} display="flex" justifyContent="flex-start">
            <Image
              src={displayPhotoUrl}
              alt={personalInfo.fullName}
              boxSize="80px"
              borderRadius="full"
              objectFit="cover"
              border="3px solid"
              borderColor={hasBg ? 'whiteAlpha.600' : settings.accentColor}
            />
          </Box>
        )}

        {/* Name & Title */}
        <VStack spacing={1} mb={6} alignItems="flex-start">
          <Box fontSize={sizes.name} fontWeight="bold" color={hasBg ? 'white' : '#1A202C'} lineHeight="1.2">
            {personalInfo.fullName || 'Your Name'}
          </Box>
          <Box fontSize={sizes.title} color={hasBg ? 'whiteAlpha.800' : '#4A5568'}>
            {personalInfo.jobTitle || 'Job Title'}
          </Box>
        </VStack>

        {/* Contact */}
        <Box mb={6}>
          <Box
            fontSize={sizes.section}
            fontWeight="bold"
            color={hasBg ? 'white' : '#1A202C'}
            letterSpacing="1px"
            mb={1}
          >
            Contact
          </Box>
          <Divider borderColor={hasBg ? 'whiteAlpha.300' : settings.accentColor} opacity={0.3} mb={2} />
          <Stack spacing={1} fontSize={sizes.body} color={hasBg ? 'whiteAlpha.900' : '#4A5568'}>
            {buildContactLinks(personalInfo).map((item, i) => (
              <HStack key={i} spacing={1.5} alignItems="center">
                <Box as="span" color={hasBg ? 'whiteAlpha.400' : settings.accentColor} fontSize="5px" flexShrink={0}>●</Box>
                {item.href ? (
                  <Box as="a" href={item.href} target="_blank" rel="noopener noreferrer" _hover={{ textDecoration: 'underline' }}>
                    {item.display}
                  </Box>
                ) : (
                  <Box>{item.display}</Box>
                )}
                <Box as="span" color={hasBg ? 'whiteAlpha.400' : settings.accentColor} fontSize="5px" flexShrink={0}>●</Box>
              </HStack>
            ))}
          </Stack>
        </Box>

        {/* Sidebar sections */}
        {sidebarSections.map((section) => renderSection(section, true))}
      </Box>

      {/* Main Content */}
      <Box flex={1} p={6}>
        {/* Ordered content: About + Main sections respecting aboutPosition */}
        {(() => {
          const aboutBlock = personalInfo.summary ? (
            <Box key="__about__" mb={4}>
              <Box
                fontSize={sizes.section}
                fontWeight="bold"
                color="#1A202C"
                letterSpacing="1px"
                mb={1}
              >
                {personalInfo.aboutTitle || 'About Me'}
              </Box>
              <Divider borderColor={settings.accentColor} opacity={0.3} mb={2} />
              <Box fontSize={sizes.body} color="gray.600" lineHeight="1.6">
                {personalInfo.summary}
              </Box>
            </Box>
          ) : null;

          // Count how many main sections come before aboutPosition in full section order
          const aboutPos = data.aboutPosition ?? 0;
          let mainIdx = 0;
          let aboutMainPos = 0;
          for (let i = 0; i < sections.length; i++) {
            if (i === aboutPos) { aboutMainPos = mainIdx; break; }
            if (!SIDEBAR_SECTION_TYPES.includes(sections[i].type)) mainIdx++;
          }
          if (aboutPos >= sections.length) aboutMainPos = mainSections.length;

          const items: React.ReactNode[] = [];
          mainSections.forEach((section, i) => {
            if (i === aboutMainPos && aboutBlock) items.push(aboutBlock);
            items.push(renderSection(section, false));
          });
          if (aboutMainPos >= mainSections.length && aboutBlock) items.push(aboutBlock);
          return items;
        })()}

        {/* GDPR Consent */}
        {personalInfo.gdprConsent && (
          <Box mt={4} pt={2} borderTop="1px solid" borderColor={`${settings.accentColor}33`}>
            <Box fontSize={sizes.gdpr} color="gray.400" lineHeight="1.5" fontStyle="italic">
              {GDPR_CONSENT_TEXT}
            </Box>
          </Box>
        )}
      </Box>
    </HStack>
  );
};

export default ModernTemplate;
